/**
 * De:maf landing page: "recent sales" social-proof toast.
 *
 * DEMO / PLACEHOLDER DATA — this custom element currently invents a buyer name, region and
 * "X minutes ago" timestamp on a timer. When wiring this up to real Shopify data, replace
 * #pickSale() with a call to your real order feed (e.g. an app proxy endpoint backed by the
 * Orders API, or a periodically-regenerated JSON snippet of recent orders) and drop the
 * NAME_POOL/#pickSale randomization below. Everything else (timing, geo lookup, dismissal,
 * markup) can stay as-is.
 */

// Older-skewing first names, grouped by country so the popup can feel locally relevant.
// Swap/extend freely — this is placeholder demo data, not a claim about who actually buys.
/** @type {Record<string, string[]>} */
const NAME_POOL = {
  US: ['Linda', 'Patricia', 'Barbara', 'Susan', 'Deborah', 'Nancy', 'Karen', 'Betty', 'Sandra', 'Carol', 'Donna', 'Ruth', 'Sharon', 'Cynthia', 'Kathleen'],
  PH: ['Corazon', 'Remedios', 'Josefina', 'Teresita', 'Luz', 'Rosario', 'Nenita', 'Perla', 'Erlinda', 'Milagros', 'Estrella', 'Gloria'],
  CA: ['Margaret', 'Joan', 'Marilyn', 'Diane', 'Judith', 'Elaine', 'Wendy', 'Janet', 'Brenda', 'Carole'],
  AU: ['Margaret', 'Judith', 'Beverley', 'Jan', 'Christine', 'Robyn', 'Lesley', 'Gaye', 'Cheryl', 'Yvonne'],
  SG: ['Mei Ling', 'Siew Hoon', 'Geok Lan', 'Kim Choo', 'Bee Choo', 'Swee Lin', 'Ah Muay', 'Poh Lian', 'Yock Lin', 'Choon Neo'],
};

// A couple of cities per region, just so the toast can say "from ___" instead of a bare
// country name. Also placeholder — a real feed would use the order's actual shipping city.
/** @type {Record<string, string[]>} */
const CITY_POOL = {
  US: ['Austin, TX', 'Columbus, OH', 'Sacramento, CA', 'Tampa, FL', 'Phoenix, AZ', 'Portland, OR'],
  PH: ['Quezon City', 'Cebu City', 'Davao City', 'Makati', 'Iloilo City'],
  CA: ['Toronto, ON', 'Calgary, AB', 'Ottawa, ON', 'Winnipeg, MB', 'Halifax, NS'],
  AU: ['Brisbane, QLD', 'Perth, WA', 'Adelaide, SA', 'Newcastle, NSW', 'Geelong, VIC'],
  SG: ['Tampines', 'Bishan', 'Toa Payoh', 'Ang Mo Kio', 'Bedok', 'Jurong East'],
};

const LAST_INITIALS = 'ABCDEGHJKLMNPRSTW'.split('');

const DEFAULT_COUNTRY = 'US';
const GEO_LOOKUP_URL = 'https://ipapi.co/json/';
const GEO_LOOKUP_TIMEOUT_MS = 2500;
const GEO_SESSION_KEY = 'lp-recent-sales-popup:country';

class LpRecentSalesPopup extends HTMLElement {
  connectedCallback() {
    if (sessionStorage.getItem(this.#dismissKey) === '1') return;

    this.#initialDelayMs = Number(this.dataset.initialDelay) || 10000;
    this.#visibleMs = Number(this.dataset.visibleDuration) || 6000;
    this.#intervalMs = Number(this.dataset.interval) || 20000;
    this.#productLabel = this.dataset.product || 'De:maf Serum';

    this.#card = this.querySelector('[data-card]');
    this.#nameEl = this.querySelector('[data-name]');
    this.#textEl = this.querySelector('[data-text]');
    this.#metaEl = this.querySelector('[data-meta]');
    this.#closeEl = this.querySelector('[data-close]');

    this.#closeEl?.addEventListener('click', () => this.#dismiss());

    this.#resolveCountry().then((country) => {
      this.#country = country;
      this.#startLoop();
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  async #resolveCountry() {
    const cached = sessionStorage.getItem(GEO_SESSION_KEY);
    if (cached) return cached;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);
      const response = await fetch(GEO_LOOKUP_URL, { signal: controller.signal });
      clearTimeout(timeout);
      if (!response.ok) throw new Error('geo lookup failed');
      const data = await response.json();
      const code = String(data?.country_code || '').toUpperCase();
      const resolved = NAME_POOL[code] ? code : DEFAULT_COUNTRY;
      sessionStorage.setItem(GEO_SESSION_KEY, resolved);
      return resolved;
    } catch {
      return DEFAULT_COUNTRY;
    }
  }

  #startLoop() {
    this.#timer = setTimeout(() => this.#showNext(), this.#initialDelayMs);
  }

  #showNext() {
    const sale = this.#pickSale();
    this.#render(sale);
    this.#card?.setAttribute('data-visible', 'true');

    this.#timer = setTimeout(() => {
      this.#card?.setAttribute('data-visible', 'false');
      this.#timer = setTimeout(() => this.#showNext(), this.#intervalMs);
    }, this.#visibleMs);
  }

  // DEMO ONLY: invents a plausible-looking recent sale. Replace with a real data source
  // before launch (see file header comment).
  #pickSale() {
    const names = NAME_POOL[this.#country] || NAME_POOL[DEFAULT_COUNTRY] || [];
    const cities = CITY_POOL[this.#country] || CITY_POOL[DEFAULT_COUNTRY] || [];
    const firstName = names[Math.floor(Math.random() * names.length)];
    const lastInitial = LAST_INITIALS[Math.floor(Math.random() * LAST_INITIALS.length)];
    const city = cities[Math.floor(Math.random() * cities.length)];
    const minutesAgo = 1 + Math.floor(Math.random() * 10); // random 1–10 minutes

    return {
      name: `${firstName} ${lastInitial}.`,
      city,
      minutesAgo,
    };
  }

  #render(sale) {
    if (this.#nameEl) this.#nameEl.textContent = sale.name;
    if (this.#textEl) {
      this.#textEl.textContent = `bought the ${this.#productLabel} ${sale.minutesAgo} minute${sale.minutesAgo === 1 ? '' : 's'} ago`;
    }
    if (this.#metaEl) this.#metaEl.textContent = sale.city;
  }

  #dismiss() {
    clearTimeout(this.#timer);
    this.#card?.setAttribute('data-visible', 'false');
    sessionStorage.setItem(this.#dismissKey, '1');
  }

  get #dismissKey() {
    return 'lp-recent-sales-popup:dismissed';
  }

  /** @type {number|undefined} */
  #timer;
  /** @type {string} */
  #country = DEFAULT_COUNTRY;
  /** @type {number} */
  #initialDelayMs = 10000;
  /** @type {number} */
  #visibleMs = 6000;
  /** @type {number} */
  #intervalMs = 20000;
  /** @type {string} */
  #productLabel = 'De:maf Serum';
  /** @type {HTMLElement|null} */
  #card = null;
  /** @type {HTMLElement|null} */
  #nameEl = null;
  /** @type {HTMLElement|null} */
  #textEl = null;
  /** @type {HTMLElement|null} */
  #metaEl = null;
  /** @type {HTMLElement|null} */
  #closeEl = null;
}

if (!customElements.get('lp-recent-sales-popup')) {
  customElements.define('lp-recent-sales-popup', LpRecentSalesPopup);
}
